import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { ISTANBUL_ILCELERI, IHALE_DURUMLARI, KONTROL_EK_SECENEKLER, IHALE_USULU_OPTIONS, ALIM_TURU_OPTIONS, SOZLESME_TURU_OPTIONS, IDARE_OPTIONS, ODENEK_DURUMU_OPTIONS, KESIN_KABUL_OPTIONS } from './constants';
import { TesisTuruSecici } from '../../TesisTuruSecici';
import { computeNakdiIlerleme } from './calc';
import { AdresKonumSecici } from '../../maps/AdresKonumSecici';

function OptionSelect({ label, value, onChange, options, placeholder = 'Seçin', testId, required, labelClass = '' }) {
  return (
    <div className="space-y-2">
      <Label className={labelClass}>{label}{required ? ' *' : ''}</Label>
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
          {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function IhaleFormDialog({
  open, onOpenChange, editMode,
  formData, setFormData,
  users, displayMoney,
  onSubmit, submitting, onCancel
}) {
  const [currentStep, setCurrentStep] = useState(1);

  // Nakdi ilerlemeyi ilgili alanlar değişince otomatik güncelle
  useEffect(() => {
    const computed = computeNakdiIlerleme(formData);
    if (computed !== formData.nakdi_ilerleme) {
      setFormData((prev) => ({ ...prev, nakdi_ilerleme: computed }));
    }
    // eslint-disable-next-line
  }, [
    formData.toplam_odenen_hakedis,
    formData.sozlesme_bedeli,
    formData.guncel_sozlesme_bedeli,
    formData.guncel_sozlesme_bedeli_1,
    formData.guncel_sozlesme_bedeli_2,
    formData.guncel_sozlesme_bedeli_3,
  ]);

  const nextStep = () => { if (currentStep < 5) setCurrentStep(currentStep + 1); };
  const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const handleOpenChange = (val) => {
    if (!val && open) return;
    onOpenChange(val);
  };

  const handleCancel = () => {
    setCurrentStep(1);
    onCancel();
  };

  const handleSubmit = () => {
    onSubmit();
    setCurrentStep(1);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="ihale-form-title">{editMode ? 'İhale Düzenle' : 'Yeni İhale Ekle'} - Adım {currentStep}/5</DialogTitle>
          <DialogDescription className="text-gray-400">
            {currentStep === 1 && 'Temel Bilgiler'}
            {currentStep === 2 && 'Sorumlular ve Lokasyon'}
            {currentStep === 3 && 'Firma ve Durum'}
            {currentStep === 4 && 'Finansal Bilgiler'}
            {currentStep === 5 && 'Detaylar ve Tamamlama'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {currentStep === 1 && <Step1 formData={formData} setFormData={setFormData} />}
          {currentStep === 2 && <Step2 formData={formData} setFormData={setFormData} users={users} />}
          {currentStep === 3 && <Step3 formData={formData} setFormData={setFormData} />}
          {currentStep === 4 && <Step4 formData={formData} setFormData={setFormData} displayMoney={displayMoney} />}
          {currentStep === 5 && <Step5 formData={formData} setFormData={setFormData} displayMoney={displayMoney} users={users} />}
        </div>

        <DialogFooter className="flex items-center justify-between">
          <div>
            {currentStep > 1 && (
              <Button onClick={prevStep} variant="outline" className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
                Geri
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCancel} variant="ghost" className="text-gray-400">İptal</Button>
            {currentStep < 5 ? (
              <Button onClick={nextStep} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ihale-form-next-btn">
                İleri
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="ihale-form-submit-btn">
                {submitting ? 'Kaydediliyor...' : (editMode ? 'Güncelle' : 'Oluştur')}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Step1({ formData, setFormData }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Yıl *</Label>
          <Input type="number" data-testid="ihale-form-yil-input" value={formData.yil} onChange={(e) => setFormData({...formData, yil: parseInt(e.target.value)})} className="bg-gray-800 border-gray-700 text-white" required />
        </div>
        <div className="space-y-2">
          <Label>İhale No *</Label>
          <Input data-testid="ihale-form-ihale-no-input" value={formData.ihale_no} onChange={(e) => setFormData({...formData, ihale_no: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="2024/1556786" required />
        </div>
      </div>
      <div className="space-y-2">
        <Label>İhale Tarihi</Label>
        <Input type="date" value={formData.ihale_tarihi} onChange={(e) => setFormData({...formData, ihale_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <OptionSelect label="İhale Usulü" required value={formData.ihale_usulu} onChange={(value) => setFormData({...formData, ihale_usulu: value})} options={IHALE_USULU_OPTIONS} testId="ihale-form-ihale-usulu-select" />
        <OptionSelect label="Alım Türü" required value={formData.alim_turu} onChange={(value) => setFormData({...formData, alim_turu: value})} options={ALIM_TURU_OPTIONS} testId="ihale-form-alim-turu-select" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Tesis Türü (çoklu)</Label>
          <TesisTuruSecici value={formData.tesis_turu} onChange={(codes) => setFormData({...formData, tesis_turu: codes})} testId="ihale-form-tesis-turu-select" />
        </div>
        <OptionSelect label="Sözleşme Türü" value={formData.sozlesme_turu} onChange={(value) => setFormData({...formData, sozlesme_turu: value})} options={SOZLESME_TURU_OPTIONS} testId="ihale-form-sozlesme-turu-select" />
      </div>
      <div className="space-y-2">
        <Label>İşin Adı *</Label>
        <Textarea data-testid="ihale-form-isin-adi-input" value={formData.isin_adi} onChange={(e) => setFormData({...formData, isin_adi: e.target.value})} className="bg-gray-800 border-gray-700 text-white min-h-20" placeholder="İşin detaylı açıklaması" required />
      </div>
    </div>
  );
}

function Step2({ formData, setFormData, users }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>İlgilenen Memur</Label>
        <Select value={formData.ilgilenen_memur} onValueChange={(value) => setFormData({...formData, ilgilenen_memur: value})}>
          <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Seçin" /></SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
            {users.length === 0 ? (
              <SelectItem value="loading" disabled>Yükleniyor...</SelectItem>
            ) : (
              users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>İlçesi *</Label>
        <Select value={formData.ilcesi} onValueChange={(value) => setFormData({...formData, ilcesi: value})}>
          <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="İlçe seçin" /></SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
            {ISTANBUL_ILCELERI.map(ilce => <SelectItem key={ilce} value={ilce}>{ilce}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <AdresKonumSecici
        enlem={formData.enlem}
        boylam={formData.boylam}
        onChange={({ enlem, boylam }) => setFormData({ ...formData, enlem, boylam })}
      />
      <OptionSelect label="İşi Yapan İdare" required value={formData.isi_yapan_idare} onChange={(value) => setFormData({...formData, isi_yapan_idare: value})} options={IDARE_OPTIONS} testId="ihale-form-idare-select" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <OptionSelect label="Ödenek Durumu" value={formData.odenek_durumu} onChange={(value) => setFormData({...formData, odenek_durumu: value})} options={ODENEK_DURUMU_OPTIONS} testId="ihale-form-odenek-durumu-select" />
        <div className="space-y-2">
          <Label className="text-blue-400">Gelen Ödenek Tarihi</Label>
          <Input
            type="date"
            data-testid="ihale-form-odenek-tarihi-input"
            value={formData.odenek_tarihi || ''}
            onChange={(e) => setFormData({...formData, odenek_tarihi: e.target.value})}
            className="bg-gray-800 border-gray-700 text-white"
          />
          <p className="text-xs text-gray-500">Gelen ödeneğin tarihini kaydedin.</p>
        </div>
      </div>
    </div>
  );
}

function Step3({ formData, setFormData }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Durumu *</Label>
        <Select value={formData.durumu} onValueChange={(value) => setFormData({...formData, durumu: value})}>
          <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Seçin" /></SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700 text-white">
            {IHALE_DURUMLARI.map(durum => <SelectItem key={durum.value} value={durum.value}>{durum.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>İhaleyi Alan Firma</Label>
        <Input value={formData.ihaleyi_alan_firma} onChange={(e) => setFormData({...formData, ihaleyi_alan_firma: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
      </div>
      <div className="space-y-2">
        <Label>Firma İletişim Adresi</Label>
        <Input value={formData.firma_iletisim_adresi} onChange={(e) => setFormData({...formData, firma_iletisim_adresi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Telefon, email, adres vb." />
      </div>
      <div className="space-y-2">
        <Label>Alt Yüklenici Firma</Label>
        <Input value={formData.alt_yuklenici_firma} onChange={(e) => setFormData({...formData, alt_yuklenici_firma: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
      </div>
    </div>
  );
}

function Step4({ formData, setFormData, displayMoney }) {
  const calcIsArtisi = (artisValue, baseBedel, suffix) => {
    const cleanedValue = artisValue.replace(/[^0-9,.-]/g, '');
    let artisNum = parseFloat(cleanedValue.replace(/\./g, '').replace(',', '.'));
    if (isNaN(artisNum)) artisNum = 0;
    const baseNum = parseFloat((baseBedel || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
    let guncelBedel = '';
    if (!isNaN(baseNum) && !isNaN(artisNum)) {
      guncelBedel = (baseNum + artisNum).toFixed(2).replace('.', ',');
    }
    let oran = '';
    if (!isNaN(baseNum) && baseNum > 0 && !isNaN(artisNum)) {
      oran = ((artisNum / baseNum) * 100).toFixed(2).replace('.', ',');
    }
    return { [`is_artis_tutari_${suffix}`]: cleanedValue, [`guncel_sozlesme_bedeli_${suffix}`]: guncelBedel, [`is_artis_orani_${suffix}`]: oran };
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Yaklaşık Maliyet</Label>
          <Input data-testid="ihale-form-yaklasik-maliyet-input" value={displayMoney(formData.yaklasik_maliyet)} onChange={(e) => setFormData({...formData, yaklasik_maliyet: e.target.value.replace(/ TL/g, '')})} className="bg-gray-800 border-gray-700 text-white" placeholder="3600848,45" />
        </div>
        <div className="space-y-2">
          <Label>Sözleşme Bedeli</Label>
          <Input data-testid="ihale-form-sozlesme-bedeli-input" value={displayMoney(formData.sozlesme_bedeli)} onChange={(e) => setFormData({...formData, sozlesme_bedeli: e.target.value.replace(/ TL/g, '')})} className="bg-gray-800 border-gray-700 text-white" />
        </div>
      </div>

      {/* 1. İş Artışı */}
      <div className="border border-red-500 rounded-lg p-4 space-y-4">
        <div className="space-y-2">
          <Label className="text-red-400">1. İş Artışı/Azalışı Var mı?</Label>
          <Select value={formData.is_artisi_1_var_mi} onValueChange={(value) => setFormData({...formData, is_artisi_1_var_mi: value})}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Seçin" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white">
              <SelectItem value="yok">Yok</SelectItem>
              <SelectItem value="var">Var</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {formData.is_artisi_1_var_mi === 'var' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>1. İş Artış/Azalış Tutarı (Azalış için &quot;-&quot; ile başlayın)</Label>
                <Input data-testid="ihale-form-is-artis-tutari-1-input" value={displayMoney(formData.is_artis_tutari_1)} onChange={(e) => {
                  const updates = calcIsArtisi(e.target.value.replace(/ TL/g, ''), formData.sozlesme_bedeli, '1');
                  setFormData({...formData, ...updates});
                }} className="bg-gray-800 border-gray-700 text-white" placeholder="100.000,00 veya -50.000,00" />
              </div>
              <div className="space-y-2">
                <Label>1. İş Artış/Azalış Oranı (%) - Otomatik</Label>
                <Input value={formData.is_artis_orani_1 ? formData.is_artis_orani_1 + ' %' : ''} disabled className="bg-gray-700 border-gray-600 text-gray-400" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>1. Güncel Sözleşme Bedeli</Label>
              <Input value={displayMoney(formData.guncel_sozlesme_bedeli_1)} disabled className="bg-gray-700 border-gray-600 text-gray-400" />
            </div>
          </>
        )}
      </div>

      {/* 2. İş Artışı */}
      <div className="border border-red-500 rounded-lg p-4 space-y-4">
        <div className="space-y-2">
          <Label className="text-red-400">2. İş Artışı/Azalışı Var mı?</Label>
          <Select value={formData.is_artisi_2_var_mi} onValueChange={(value) => setFormData({...formData, is_artisi_2_var_mi: value})}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Seçin" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white">
              <SelectItem value="yok">Yok</SelectItem>
              <SelectItem value="var">Var</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {formData.is_artisi_2_var_mi === 'var' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>2. İş Artış/Azalış Tutarı</Label>
                <Input data-testid="ihale-form-is-artis-tutari-2-input" value={displayMoney(formData.is_artis_tutari_2)} onChange={(e) => {
                  const base = formData.guncel_sozlesme_bedeli_1 || formData.sozlesme_bedeli;
                  const updates = calcIsArtisi(e.target.value.replace(/ TL/g, ''), base, '2');
                  setFormData({...formData, ...updates});
                }} className="bg-gray-800 border-gray-700 text-white" placeholder="50.000,00" />
              </div>
              <div className="space-y-2">
                <Label>2. İş Artış/Azalış Oranı (%) - Otomatik</Label>
                <Input value={formData.is_artis_orani_2 ? formData.is_artis_orani_2 + ' %' : ''} disabled className="bg-gray-700 border-gray-600 text-gray-400" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>2. Güncel Sözleşme Bedeli</Label>
              <Input value={displayMoney(formData.guncel_sozlesme_bedeli_2)} disabled className="bg-gray-700 border-gray-600 text-gray-400" />
            </div>
          </>
        )}
      </div>

      {/* 3. İş Artışı */}
      <div className="border border-red-500 rounded-lg p-4 space-y-4">
        <div className="space-y-2">
          <Label className="text-red-400">3. İş Artışı/Azalışı Var mı?</Label>
          <Select value={formData.is_artisi_3_var_mi} onValueChange={(value) => setFormData({...formData, is_artisi_3_var_mi: value})}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Seçin" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white">
              <SelectItem value="yok">Yok</SelectItem>
              <SelectItem value="var">Var</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {formData.is_artisi_3_var_mi === 'var' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>3. İş Artış/Azalış Tutarı</Label>
                <Input data-testid="ihale-form-is-artis-tutari-3-input" value={displayMoney(formData.is_artis_tutari_3)} onChange={(e) => {
                  const base = formData.guncel_sozlesme_bedeli_2 || formData.guncel_sozlesme_bedeli_1 || formData.sozlesme_bedeli;
                  const updates = calcIsArtisi(e.target.value.replace(/ TL/g, ''), base, '3');
                  setFormData({...formData, ...updates});
                }} className="bg-gray-800 border-gray-700 text-white" placeholder="30.000,00" />
              </div>
              <div className="space-y-2">
                <Label>3. İş Artış/Azalış Oranı (%) - Otomatik</Label>
                <Input value={formData.is_artis_orani_3 ? formData.is_artis_orani_3 + ' %' : ''} disabled className="bg-gray-700 border-gray-600 text-gray-400" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>3. Güncel Sözleşme Bedeli (FINAL)</Label>
              <Input value={displayMoney(formData.guncel_sozlesme_bedeli_3)} disabled className="bg-gray-700 border-gray-600 text-gray-400" />
            </div>
          </>
        )}
      </div>

      {/* Toplam Özet */}
      <div className="bg-blue-900/20 border border-blue-500 rounded-lg p-4 space-y-3">
        <h3 className="text-blue-400 font-medium">Toplam Özet</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-blue-300">Toplam İş Artış/Azalış Oranı (%)</Label>
            <Input value={(() => {
              const oran1 = parseFloat((formData.is_artis_orani_1 || '0').replace(',', '.'));
              const oran2 = parseFloat((formData.is_artis_orani_2 || '0').replace(',', '.'));
              const oran3 = parseFloat((formData.is_artis_orani_3 || '0').replace(',', '.'));
              const toplam = oran1 + oran2 + oran3;
              return toplam !== 0 ? toplam.toFixed(2).replace('.', ',') + ' %' : '';
            })()} disabled className="bg-blue-900/30 border-blue-600 text-blue-200 font-bold" />
          </div>
          <div className="space-y-2">
            <Label className="text-blue-300">Toplam Sözleşme Bedeli (Son Güncel)</Label>
            <Input value={(() => {
              const sonGuncel = formData.guncel_sozlesme_bedeli_3 || formData.guncel_sozlesme_bedeli_2 || formData.guncel_sozlesme_bedeli_1 || formData.sozlesme_bedeli;
              return displayMoney(sonGuncel);
            })()} disabled className="bg-blue-900/30 border-blue-600 text-blue-200 font-bold" />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Toplam Ödenen Hakediş Tutarı (KDV Dahil)</Label>
        <Input
          data-testid="ihale-form-hakedis-input"
          value={displayMoney(formData.toplam_odenen_hakedis)}
          onChange={(e) => {
            const hakedis = e.target.value.replace(/ TL/g, '');
            setFormData({ ...formData, toplam_odenen_hakedis: hakedis });
          }}
          className="bg-gray-800 border-gray-700 text-white"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nakdi İlerleme (%) - Otomatik</Label>
          <Input value={formData.nakdi_ilerleme ? formData.nakdi_ilerleme + ' %' : ''} placeholder="Önce Sözleşme Bedeli ve Hakediş girin" disabled className="bg-gray-700 border-gray-600 text-gray-400" />
        </div>
        <div className="space-y-2">
          <Label>Fiziki İlerleme (%)</Label>
          <Input value={formData.fiziki_ilerleme} onChange={(e) => setFormData({...formData, fiziki_ilerleme: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="75" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Fiyat Farkı</Label>
          <Select value={formData.fiyat_farki} onValueChange={(value) => setFormData({...formData, fiyat_farki: value})}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Seçin" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white">
              <SelectItem value="var">VAR</SelectItem>
              <SelectItem value="yok">YOK</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Fiyat Farkı Tutarı</Label>
          <Input value={displayMoney(formData.fiyat_farki_tutari)} onChange={(e) => setFormData({...formData, fiyat_farki_tutari: e.target.value.replace(/ TL/g, '')})} className="bg-gray-800 border-gray-700 text-white" />
        </div>
      </div>

      {/* İş Artışı/Süre Uzatımı */}
      <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg space-y-4">
        <div className="space-y-2">
          <Label>İş Artışı/Süre Uzatımı Var mı?</Label>
          <Select value={formData.is_artisi_var_mi} onValueChange={(value) => setFormData({...formData, is_artisi_var_mi: value})}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Seçin" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white">
              <SelectItem value="var">VAR</SelectItem>
              <SelectItem value="yok">YOK</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {formData.is_artisi_var_mi === 'var' && (
          <div className="grid grid-cols-3 gap-4 pt-3 border-t border-yellow-700/30">
            <div className="space-y-2">
              <Label className="text-sm">Ek Kesin Teminat</Label>
              <Select value={formData.ek_kesin_teminat} onValueChange={(value) => setFormData({...formData, ek_kesin_teminat: value})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-9"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="var">VAR</SelectItem>
                  <SelectItem value="yok">YOK</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Revize İş Programı</Label>
              <Select value={formData.revize_is_programi} onValueChange={(value) => setFormData({...formData, revize_is_programi: value})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-9"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="var">VAR</SelectItem>
                  <SelectItem value="yok">YOK</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">İlave ALL Risk Poliçesi</Label>
              <Select value={formData.ilave_all_risk} onValueChange={(value) => setFormData({...formData, ilave_all_risk: value})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-9"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="var">VAR</SelectItem>
                  <SelectItem value="yok">YOK</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Step5({ formData, setFormData, displayMoney, users }) {
  const kontrolSecenekleri = [...users, ...KONTROL_EK_SECENEKLER];
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Sözleşme Tarihi</Label>
        <Input type="date" value={formData.sozlesme_tarihi} onChange={(e) => setFormData({...formData, sozlesme_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Yer Teslim Tarihi</Label>
          <Input type="date" value={formData.yer_teslim_tarihi} onChange={(e) => {
            const yerTeslim = e.target.value;
            let bitisTarihi = formData.bitis_tarihi;
            if (yerTeslim && formData.isin_suresi) {
              const teslimDate = new Date(yerTeslim);
              teslimDate.setDate(teslimDate.getDate() + parseInt(formData.isin_suresi));
              bitisTarihi = teslimDate.toISOString().split('T')[0];
            }
            setFormData({...formData, yer_teslim_tarihi: yerTeslim, bitis_tarihi: bitisTarihi});
          }} className="bg-gray-800 border-gray-700 text-white" />
        </div>
        <div className="space-y-2">
          <Label>İşin Süresi (Gün)</Label>
          <Input type="number" value={formData.isin_suresi} onChange={(e) => {
            const sure = e.target.value;
            let bitisTarihi = formData.bitis_tarihi;
            if (formData.yer_teslim_tarihi && sure) {
              const teslimDate = new Date(formData.yer_teslim_tarihi);
              teslimDate.setDate(teslimDate.getDate() + parseInt(sure));
              bitisTarihi = teslimDate.toISOString().split('T')[0];
            }
            setFormData({...formData, isin_suresi: sure, bitis_tarihi: bitisTarihi});
          }} className="bg-gray-800 border-gray-700 text-white" placeholder="180" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Bitiş Tarihi (Otomatik)</Label>
          <Input type="date" value={formData.bitis_tarihi} disabled className="bg-gray-700 border-gray-600 text-gray-400" />
        </div>
        <div className="space-y-2">
          <Label>Süre Uzatımı (Gün)</Label>
          <Input type="number" value={formData.ilave_sure} onChange={(e) => {
            const sureUzatimi = e.target.value;
            let ilaveBitim = '';
            if (formData.bitis_tarihi && sureUzatimi) {
              const bitisDate = new Date(formData.bitis_tarihi);
              bitisDate.setDate(bitisDate.getDate() + parseInt(sureUzatimi));
              ilaveBitim = bitisDate.toISOString().split('T')[0];
            }
            setFormData({...formData, ilave_sure: sureUzatimi, sure_uzatimi: ilaveBitim});
          }} className="bg-gray-800 border-gray-700 text-white" placeholder="30" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>İlave Süreli İş Bitim Tarihi (Otomatik)</Label>
        <Input type="date" value={formData.sure_uzatimi} disabled className="bg-gray-700 border-gray-600 text-gray-400" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Geçici Kabul Tarihi</Label>
          <Input type="date" value={formData.gecici_kabul_tarihi} onChange={(e) => {
            const gecici = e.target.value;
            let kesin = formData.kesin_kabul_tarihi;
            if (gecici && formData.teminat_suresi_ay) {
              const geciciDate = new Date(gecici);
              geciciDate.setMonth(geciciDate.getMonth() + parseInt(formData.teminat_suresi_ay));
              kesin = geciciDate.toISOString().split('T')[0];
            }
            setFormData({...formData, gecici_kabul_tarihi: gecici, kesin_kabul_tarihi: kesin});
          }} className="bg-gray-800 border-gray-700 text-white" />
        </div>
        <div className="space-y-2">
          <Label>Teminat Süresi (Ay)</Label>
          <Input type="number" value={formData.teminat_suresi_ay} onChange={(e) => {
            const ay = e.target.value;
            let kesin = formData.kesin_kabul_tarihi;
            if (formData.gecici_kabul_tarihi && ay) {
              const geciciDate = new Date(formData.gecici_kabul_tarihi);
              geciciDate.setMonth(geciciDate.getMonth() + parseInt(ay));
              kesin = geciciDate.toISOString().split('T')[0];
            }
            setFormData({...formData, teminat_suresi_ay: ay, kesin_kabul_tarihi: kesin});
          }} className="bg-gray-800 border-gray-700 text-white" placeholder="12" />
        </div>
        <div className="space-y-2">
          <Label>Kesin Kabul Tarihi (Otomatik)</Label>
          <Input type="date" value={formData.kesin_kabul_tarihi} disabled className="bg-gray-700 border-gray-600 text-gray-400" />
        </div>
      </div>
      <OptionSelect label="Kesin Kabul Durumu" value={formData.kesin_kabul_durumu} onChange={(value) => setFormData({...formData, kesin_kabul_durumu: value})} options={KESIN_KABUL_OPTIONS} testId="ihale-form-kesin-kabul-select" />
      <div className="space-y-2">
        <Label>Kesin Teminat Mektubu (TL)</Label>
        <Input value={displayMoney(formData.kesin_teminat_mektubu)} onChange={(e) => setFormData({...formData, kesin_teminat_mektubu: e.target.value.replace(/ TL/g, '')})} className="bg-gray-800 border-gray-700 text-white" placeholder="150.000,00" />
      </div>

      {/* Kontrol Teşkilatı */}
      <div className="space-y-3 p-4 bg-gray-800/50 rounded-lg">
        <div className="flex items-center justify-between">
          <Label className="text-white font-medium">Kontrol Teşkilatı (Çoklu Seçim):</Label>
          <Button type="button" size="sm" onClick={() => {
            if (formData.kontrol_teskilati.length === kontrolSecenekleri.length) {
              setFormData({...formData, kontrol_teskilati: []});
            } else {
              setFormData({...formData, kontrol_teskilati: kontrolSecenekleri.map(u => u.id)});
            }
          }} variant="outline" className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
            {formData.kontrol_teskilati.length === kontrolSecenekleri.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
          {kontrolSecenekleri.map(u => (
            <div key={u.id} className="flex items-center space-x-2">
              <input type="checkbox" id={`kontrol_${u.id}`} checked={formData.kontrol_teskilati.includes(u.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({...formData, kontrol_teskilati: [...formData.kontrol_teskilati, u.id]});
                  } else {
                    setFormData({...formData, kontrol_teskilati: formData.kontrol_teskilati.filter(id => id !== u.id)});
                  }
                }} className="w-4 h-4 rounded border-gray-600 bg-gray-700" />
              <label htmlFor={`kontrol_${u.id}`} className="text-sm text-gray-300 cursor-pointer">{u.name}</label>
            </div>
          ))}
        </div>
        {formData.kontrol_teskilati.length > 0 && (
          <p className="text-xs text-blue-400 mt-2">{formData.kontrol_teskilati.length} seçim yapıldı</p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Açıklama</Label>
        <Textarea data-testid="ihale-form-aciklama-input" value={formData.aciklama} onChange={(e) => setFormData({...formData, aciklama: e.target.value})} className="bg-gray-800 border-gray-700 text-white min-h-24" placeholder="Ek notlar ve açıklamalar" />
      </div>
    </div>
  );
}
